// One row in the RFQ list.
//
// Extracted from the inline `.map()` in rfq-list.tsx so it can be rendered in
// two places: the normal keyset-paginated list, and the flagged priority board
// where each row is drag-sortable. A sortable row needs a stable component with
// a slot for a drag handle; inline JSX inside a .map() gives you neither.
//
// PURE PRESENTATION — no data fetching, no mutations. The parent owns the <li>
// so that dnd-kit's useSortable() can attach its ref and transform to the list
// item without this component knowing drag exists. The only concession is the
// optional `dragHandle` slot.
//
// Palette note: the four hardcoded Tailwind palette classes this markup used to
// carry (bg-amber-500, bg-orange-100, bg-emerald-100, bg-amber-100) are gone,
// replaced by the semantic .pill-* classes from index.css. docs/design/
// ui-conventions.md §1 mandates tokens and says new code must not add to the
// ~100-class backlog — moving code counts as new code, and converting here
// means the diff gets reviewed once rather than twice.

import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { CopyButton } from '@/components/copy-button';
import { SourcingChannelChips } from '@/components/sourcing-channel-chips';
import { STAGE_LABEL, timeAgo } from '@/lib/format';
import type { RfqListItem } from '@/lib/api';

interface Props {
  rfq: RfqListItem;
  isSelected: boolean;
  /**
   * Rendered before the ref line. The flagged board passes a grab handle here;
   * the normal list passes nothing. Kept as a slot rather than a boolean so the
   * board owns its own dnd-kit listeners and this component stays drag-agnostic.
   */
  dragHandle?: ReactNode;
  /**
   * Rendered on the TRAILING edge, after the link. The priority toggle goes
   * here. A slot for the same reason `dragHandle` is one: the owning list holds
   * the mutation, so this component keeps its no-mutations rule, and the button
   * stays a SIBLING of the anchor rather than a child of it.
   */
  flagToggle?: ReactNode;
}

/** Confidence below this reads as "the parser was guessing" and is surfaced. */
const LOW_CONFIDENCE = 0.6;

export function RfqRow({ rfq, isSelected, dragHandle, flagToggle }: Props) {
  return (
    // The handle is a SIBLING of the link, never a child. A <button> nested
    // inside an <a> is invalid HTML — interactive content cannot descend from an
    // anchor — and browsers resolve it inconsistently: activating the handle can
    // fire the anchor's navigation, and screen readers announce a control that
    // is simultaneously a link and a button. Keeping them siblings also means
    // dnd-kit's keyboard bindings receive their keydown events intact.
    <div
      className={
        'flex items-stretch border-b border-border ' + (isSelected ? 'bg-muted' : '')
      }
    >
      {dragHandle}
      {/*
        className goes on <Link>, NOT on a nested <a>.
        wouter's Link only clones its child when `asChild` is set; otherwise it
        renders its OWN <a> around the children (node_modules/wouter/src/
        index.js:308). So `<Link><a className=…>` produces NESTED ANCHORS, and
        the element that actually participates in this flex row is wouter's
        outer anchor — which carries none of our classes. An anchor defaults to
        `flex: 0 1 auto`, so it shrank to content width and only part of the row
        was clickable.

        This was invisible before the flex container existed: the outer anchor
        sat in normal flow and the display:block child filled it. Passing
        className straight to Link yields ONE anchor that is both the flex item
        and the click target.
      */}
      <Link
        href={`/rfqs/${rfq.id}`}
        className={
          'block flex-1 min-w-0 py-3 transition-colors hover:bg-muted/50 ' +
          (dragHandle ? 'pl-1 pr-4' : 'px-4')
        }
      >
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-mono text-sm font-medium truncate">{rfq.ref}</span>
            <span className="text-xs text-muted-foreground shrink-0">{timeAgo(rfq.receivedAt)}</span>
          </div>

        <div className="mt-1 text-sm truncate">
          {rfq.customer?.displayName ?? (
            <span className="text-muted-foreground italic">Unknown customer</span>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
          {rfq.isAog && <span className="pill pill-danger font-semibold">AOG</span>}
          {rfq.isUrgent && !rfq.isAog && (
            <span className="pill pill-warning font-semibold">URGENT</span>
          )}
          {rfq.isFlagged && (
            // pill-info, not pill-warning: AOG and URGENT are urgency the
            // CUSTOMER declared, whereas a flag is a marker the broker set.
            // Different meaning deserves a different colour.
            <span
              className="pill pill-info font-semibold"
              title={rfq.flagNote ?? 'Flagged for follow-up'}
            >
              🚩 {rfq.flagNote ? rfq.flagNote.slice(0, 40) : 'Follow up'}
            </span>
          )}
          {rfq.quoteSent && (
            <span
              className="pill pill-success font-semibold"
              title={
                rfq.quoteSentAt
                  ? `Quote sent ${new Date(rfq.quoteSentAt).toLocaleDateString()}`
                  : 'Quote sent'
              }
            >
              ✅ Quote sent
            </span>
          )}
          <span className="text-muted-foreground">
            {rfq.lineCount} {rfq.lineCount === 1 ? 'line' : 'lines'}
          </span>
          {rfq.stageCounts.map(({ stage, count }) => (
            <span key={stage} className="pill pill-default">
              {count} {STAGE_LABEL[stage]}
            </span>
          ))}
          <SourcingChannelChips
            postedChannels={rfq.sourcingChannels}
            variant="readonly"
            hideUnposted
          />
          {rfq.parserConfidence !== null && rfq.parserConfidence < LOW_CONFIDENCE && (
            <span className="pill pill-warning">Low confidence</span>
          )}
        </div>

        {rfq.matchingLines.length > 0 && (
          <div className="mt-1.5 text-xs text-muted-foreground space-y-0.5">
            {rfq.matchingLines.slice(0, 3).map((l) => (
              <div key={l.id} className="font-mono truncate flex items-center gap-1">
                <span>#{l.lineNumber} · {l.partNumber}</span>
                <CopyButton value={l.partNumber} label={`Copy ${l.partNumber}`} size="xs" />
                {l.altPartNumber && (
                  <>
                    <span>/ {l.altPartNumber}</span>
                    <CopyButton value={l.altPartNumber} label={`Copy ${l.altPartNumber}`} size="xs" />
                  </>
                )}
              </div>
            ))}
            {rfq.matchingLines.length > 3 && (
              <div>+ {rfq.matchingLines.length - 3} more matching lines</div>
            )}
            </div>
          )}
      </Link>
      {/* Trailing sibling, for the same invalid-nesting reason as the handle. */}
      {flagToggle}
    </div>
  );
}
